import React, { useCallback, useMemo, useState, useRef } from 'react';
import { MessageSquare, Pencil, Timer, Trash2 } from 'lucide-react';
import { MaintenanceIntervalType, CostUnit } from '../../../../../types/Enums';
import { translateIntervalType } from '../../../../../Helpers/maintenanceCostUtils';
import { ActivityCommentLine } from './ActivityCommentLine';
import { QuantityPopover } from './QuantityPopover';
import { CostSourceChip } from '../../../components/CostSourceChip';
import type { CostBasis } from '../../contexts/ComponentCardContext';
import { MIXED_VALUE } from '../../utils/bulkSelection';

const MIN_YEAR = new Date().getFullYear();
const MAX_YEAR = MIN_YEAR + 50; // 50 year planning horizon

interface MaintenanceRowProps {
    templateName: string;
    /** The template was deleted while in use: the row stays until the user removes it, nothing new is scheduled from it */
    isTemplateArchived?: boolean;
    /** The component is excluded from the plan: the row is greyed and cannot be edited */
    isDisabled: boolean;
    isOverdue: boolean;
    isHighlighted: boolean;
    displayInterval: string;
    displayDate: string;
    displayCost: string;
    costExplanation: string;
    costBasis: CostBasis;
    displayIntervalType: MaintenanceIntervalType;
    isDateInvalid: boolean;
    /** Removes the row from the component */
    onRemove: () => void;
    onDateChange: (value: string) => void;
    onDateBlur: () => void;
    onOpenCostSettings: () => void;
    onOpenIntervalSettings: () => void;
    /** Opens the template editor; the pencil is shown only when given (admins) */
    onEditTemplate?: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    // Quantity (Mängd) column props: the quantity is the activity's own, never the component's
    displayArea: string;
    /** The component's own quantity in the row's unit, shown for reference; never edited here */
    componentQuantity: number | null;
    customQuantity: number | undefined;
    costUnit: CostUnit;
    hasCustomCost: boolean;
    onQuantityChange: (quantity: number) => void;
    onQuantityClear: () => void;
    /** The row's free-text comment, shown as a hint line under the activity */
    comment: string | undefined;
    /** False until the draft has an entry for the row to store the comment on */
    canComment: boolean;
    onCommentChange: (text: string) => void;
    /**
     * Massredigering: how many of the selected components have the row. The
     * quantity is per component and cannot be edited; a row on only some of
     * them carries a badge and can be put on all.
     */
    bulkPresence?: { present: number; total: number; onAddToAll: () => void };
}

/**
 * Maintenance activity row component
 * Displays and allows editing of maintenance activity details
 */
export const MaintenanceRow: React.FC<MaintenanceRowProps> = ({
    templateName,
    isTemplateArchived,
    isDisabled,
    isOverdue,
    isHighlighted,
    displayInterval,
    displayDate,
    displayCost,
    costExplanation,
    costBasis,
    displayIntervalType,
    isDateInvalid,
    onRemove,
    onDateChange,
    onDateBlur,
    onOpenCostSettings,
    onOpenIntervalSettings,
    onEditTemplate,
    onMouseEnter,
    onMouseLeave,
    displayArea,
    componentQuantity,
    customQuantity,
    costUnit,
    hasCustomCost,
    onQuantityChange,
    onQuantityClear,
    comment,
    canComment,
    onCommentChange,
    bulkPresence
}) => {
    const [isEditingComment, setIsEditingComment] = useState(false);
    // Pressing the icon while the field is open saves and closes it; the click must not reopen it
    const commentOpenAtPress = useRef(false);
    const rowRef = useRef<HTMLDivElement>(null);

    // Area popover state
    const [isAreaPopoverOpen, setIsAreaPopoverOpen] = useState(false);
    const areaButtonRef = useRef<HTMLButtonElement>(null);
    const closeAreaPopover = useCallback(() => setIsAreaPopoverOpen(false), []);

    // A per_unit row's quantity is its antal: a cost multiplier, never the component's count
    const isPerUnit = costUnit === 'per_unit';
    const hasCustomQuantity = customQuantity !== undefined && customQuantity > 0;
    const unitLabel = isPerUnit ? 'st' : costUnit === 'per_lm' ? 'lm' : 'm²';
    const componentQuantityText = componentQuantity
        ? `Komponentens mängd: ${componentQuantity.toLocaleString('sv-SE')} ${unitLabel}`
        : 'Komponentens mängd saknas';
    const customQuantityTitle = isPerUnit
        ? 'Anpassat antal för aktiviteten'
        : `Anpassad för aktiviteten. ${componentQuantityText}`;
    // Generate year options for the year picker
    const yearOptions = useMemo(() => {
        const years: number[] = [];
        for (let year = MIN_YEAR; year <= MAX_YEAR; year++) {
            years.push(year);
        }
        return years;
    }, []);

    // Massredigering: a value the selected components disagree on reads "Blandade"
    const isMixedDate = displayDate === MIXED_VALUE;
    const isMixedInterval = displayInterval === MIXED_VALUE;
    const isPartial = bulkPresence !== undefined && bulkPresence.present < bulkPresence.total;

    // Get the current year value from displayDate
    const currentYearValue = displayDate && !isMixedDate ? new Date(displayDate).getFullYear() : '';

    return (
        <div
            ref={rowRef}
            className={`activity-row maintenance-row ${isOverdue ? 'overdue' : ''} ${isDisabled ? 'disabled' : ''} ${isHighlighted ? 'highlighted' : ''}`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="activity-name">
                <span className={isDisabled ? 'text-muted' : ''}>{templateName || 'Namnlös mall'}</span>
                {isTemplateArchived && (
                    <span className="badge bg-secondary fw-normal" title="Mallen är borttagen. Ta bort aktiviteten om den inte ska vara kvar i planen.">
                        Arkiverad mall
                    </span>
                )}
                {isPartial && bulkPresence && (
                    <>
                        <span className="bulk-row-presence" title="Hur många av de valda komponenterna som har aktiviteten">
                            på {bulkPresence.present} av {bulkPresence.total}
                        </span>
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 bulk-row-add-all"
                            onClick={bulkPresence.onAddToAll}
                            disabled={isDisabled}
                        >
                            Lägg till på alla
                        </button>
                    </>
                )}
                {onEditTemplate && (
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onEditTemplate}
                        disabled={isDisabled}
                        title="Redigera mall"
                        aria-label={`Redigera mallen ${templateName || 'Namnlös mall'}`}
                        style={{ lineHeight: 1 }}
                    >
                        <Pencil size={14} className={isDisabled ? 'text-muted' : 'text-primary'} />
                    </button>
                )}
                {/* The span carries the tooltip: a disabled button shows none */}
                <span
                    className="d-inline-flex"
                    title={!canComment ? 'Kommentar kan läggas till när komponenten har skick och livslängd' : undefined}
                >
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0 activity-comment-button"
                        onMouseDown={() => { commentOpenAtPress.current = isEditingComment; }}
                        onClick={(e) => {
                            // A keyboard click (detail 0) has no press: ignore a flag left by a press dragged off the icon
                            if (e.detail > 0 && commentOpenAtPress.current) {
                                // Safari and iOS may not blur the field on the press: blur it, which saves and closes
                                const active = document.activeElement;
                                if (active instanceof HTMLElement && rowRef.current?.contains(active)) active.blur();
                            } else {
                                setIsEditingComment(true);
                            }
                            commentOpenAtPress.current = false;
                        }}
                        disabled={isDisabled || !canComment}
                        title={canComment ? (comment ? 'Redigera kommentar' : 'Lägg till kommentar') : undefined}
                        aria-label={`${comment ? 'Redigera kommentaren till' : 'Lägg till kommentar till'} ${templateName || 'Namnlös mall'}`}
                        style={{ lineHeight: 1 }}
                    >
                        <MessageSquare
                            size={14}
                            className={isDisabled || !canComment ? 'text-muted' : 'text-primary'}
                            fill={comment ? 'currentColor' : 'none'}
                        />
                    </button>
                </span>
            </div>

            <div className="activity-interval">
                <div className="interval-display-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span className={`interval-value ${isDisabled || isMixedInterval ? 'text-muted' : ''}`}>
                        {isMixedInterval ? MIXED_VALUE : `${displayInterval} ${translateIntervalType(displayIntervalType)}`}
                    </span>
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onOpenIntervalSettings}
                        disabled={isDisabled}
                        title="Redigera intervall"
                        style={{ lineHeight: 1 }}
                    >
                        <Pencil size={14} className={isDisabled ? 'text-muted' : 'text-primary'} />
                    </button>
                </div>
            </div>

            <div className="activity-area" style={{ position: 'relative' }}>
                {hasCustomCost ? (
                    <span className="text-muted" title="Kostnad enligt offert används">–</span>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <span
                                className={isDisabled ? 'text-muted' : ''}
                                style={{
                                    color: hasCustomQuantity && !isDisabled ? '#0d6efd' : undefined,
                                    fontWeight: hasCustomQuantity ? 500 : 'normal',
                                    cursor: hasCustomQuantity ? 'help' : undefined
                                }}
                                title={hasCustomQuantity ? customQuantityTitle : undefined}
                            >
                                {displayArea}
                            </span>
                            {/* The quantity is per component: summed, never edited, in Massredigering */}
                            {!bulkPresence && (
                                <button
                                    ref={areaButtonRef}
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    onClick={() => setIsAreaPopoverOpen(!isAreaPopoverOpen)}
                                    disabled={isDisabled}
                                    title={isPerUnit ? 'Redigera antal för aktiviteten' : 'Redigera mängd för aktiviteten'}
                                    aria-label={`Redigera ${isPerUnit ? 'antal' : 'mängd'} för ${templateName || 'Namnlös mall'}`}
                                    style={{ lineHeight: 1 }}
                                >
                                    <Pencil size={14} className={isDisabled ? 'text-muted' : 'text-primary'} />
                                </button>
                            )}
                        </div>

                        {/* Area popover */}
                        {isAreaPopoverOpen && !bulkPresence && (
                            <QuantityPopover
                                label={isPerUnit ? 'Antal (st)' : 'Mängd för denna aktivitet'}
                                hints={isPerUnit
                                    ? ['Används bara för kostnadsberäkningen i underhållsplanen. Ändrar inte antalet komponenter i byggnaden.']
                                    : ['Gäller bara kostnaden för den här aktiviteten. Komponentens mängd ändras inte.', componentQuantityText]}
                                initialValue={customQuantity ?? componentQuantity}
                                unitLabel={unitLabel}
                                wholeNumbers={isPerUnit}
                                canReset={hasCustomQuantity}
                                resetTitle={isPerUnit ? 'Återställ till 1 st' : 'Återställ till komponentens mängd'}
                                onSave={onQuantityChange}
                                onReset={onQuantityClear}
                                onClose={closeAreaPopover}
                                anchorRef={areaButtonRef}
                            />
                        )}
                    </>
                )}
            </div>

            <div className="activity-year">
                {displayIntervalType === MaintenanceIntervalType.years ? (
                    <select
                        className={`form-select form-select-sm ${isDateInvalid ? 'is-invalid' : ''}`}
                        value={currentYearValue}
                        onChange={(e) => {
                            const year = parseInt(e.target.value, 10);
                            if (!isNaN(year)) {
                                // Convert year to date string (January 1st of that year)
                                onDateChange(`${year}-01-01`);
                            }
                        }}
                        onBlur={onDateBlur}
                        disabled={isDisabled}
                        style={{ textAlign: 'center' }}
                    >
                        <option value="">{isMixedDate ? MIXED_VALUE : 'Välj år'}</option>
                        {yearOptions.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type="date"
                        className={`form-control form-control-sm ${isDateInvalid ? 'is-invalid' : ''}`}
                        value={isMixedDate ? '' : displayDate}
                        onChange={(e) => onDateChange(e.target.value)}
                        onBlur={onDateBlur}
                        disabled={isDisabled}
                        title={isMixedDate ? `${MIXED_VALUE} datum` : undefined}
                        aria-label={isMixedDate ? `Nästa datum, ${MIXED_VALUE.toLowerCase()}` : undefined}
                    />
                )}
            </div>

            <div className="activity-cost">
                <div className="cost-display-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span className={`cost-value-wrapper ${isDisabled ? 'text-muted' : ''}`}>
                        <span className="cost-value" style={{ cursor: 'help' }}>
                            <CostSourceChip source={costBasis.source} hasAttachments={costBasis.hasAttachments} muted={isDisabled}>
                                {displayCost ? `${parseInt(displayCost).toLocaleString('sv-SE')} kr` : '0 kr'}
                            </CostSourceChip>
                        </span>
                        <span className="cost-tooltip">{costExplanation}</span>
                    </span>
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onOpenCostSettings}
                        disabled={isDisabled}
                        title="Redigera kostnadsinställningar"
                        style={{ lineHeight: 1 }}
                    >
                        <Pencil size={14} className={isDisabled ? 'text-muted' : 'text-primary'} />
                    </button>
                </div>
            </div>

            <div className="activity-overdue-indicator">
                {isOverdue && !isDisabled && (
                    <span title="Försenad" style={{ lineHeight: 1 }}>
                        <Timer size={14} className="text-danger" />
                    </span>
                )}
                <button
                    type="button"
                    className="btn btn-link btn-sm p-0 activity-remove-button"
                    onClick={onRemove}
                    disabled={isDisabled}
                    title="Ta bort aktivitet"
                    aria-label={`Ta bort aktiviteten ${templateName || 'Namnlös mall'}`}
                >
                    <Trash2 size={14} />
                </button>
            </div>

            <ActivityCommentLine
                comment={comment}
                isEditing={isEditingComment}
                disabled={isDisabled || !canComment}
                onStartEdit={() => setIsEditingComment(true)}
                onSave={(text) => {
                    setIsEditingComment(false);
                    onCommentChange(text);
                }}
                onCancel={() => setIsEditingComment(false)}
            />
        </div>
    );
};
